import moment from "moment";

import { ISortDefinition } from ".";
import * as DateHelper from "@travellocal/utils/dateHelpers";

export class Sort {
  private static compareDateDesc<T>(aTime: IDate|IDateTime, bTime: IDate|IDateTime) {
    if (!aTime && !bTime) {
      return 0;
    }

    if (!aTime && bTime) {
      return 1;
    }

    if (aTime && !bTime) {
      return -1;
    }

    return Sort.compareMomentDesc(DateHelper.toMoment(aTime), DateHelper.toMoment(bTime));
  }

  /**
   * TODO handle invalid dates
   */
  private static compareMomentDesc<T>(ma: moment.Moment, mb: moment.Moment) {
    if (!ma && !mb) {
      return 0;
    }

    if (!ma && mb) {
      return 1;
    }

    if (ma && !mb) {
      return -1;
    }

    if (ma.isBefore(mb)) {
      return 1;
    }
    else if (mb.isBefore(ma)) {
      return -1;
    }
    else {
      return 0;
    }
  }

  public static chain<T>(description: string, icomoon: string, sorts: ISortDefinition<T, any>[]): ISortDefinition<T, any> {
    const compare = (a, b) => {
      for (const def of sorts) {
        const result = def.compare(a, b);
        if (result !== 0) {
          return result;
        }
      }
      return 0;
    };

    return {
      description,
      icomoon,
      compare,
    };
  }

  public static sortAlphaAsc<T>(onField: (item: T) => string): ISortDefinition<T, any> {
    return {
      description: "in alphabetical order",
      icomoon: "sort-alpha-asc",
      compare: (a, b) => (onField(a) || "").localeCompare((onField(b) || "")),
    };
  }

  public static sortAlphaDesc<T>(onField: (item: T) => string): ISortDefinition<T, any> {
    return {
      description: "in reverse-alphabetical order",
      icomoon: "sort-alpha-desc",
      compare: (a, b) => (onField(b) || "").localeCompare((onField(a) || "")),
    };
  }

  public static sortNumericAsc<T>(onField: (item: T) => number): ISortDefinition<T, any> {
    return {
      description: "in ascending order",
      icomoon: "sort-numeric-asc",
      compare: (a, b) => (onField(a) || 0) - (onField(b) || 0),
    };
  }

  public static sortNumericDesc<T>(onField: (item: T) => number): ISortDefinition<T, any> {
    return {
      description: "in descending order",
      icomoon: "sort-numberic-desc",
      compare: (a, b) => (onField(b) || 0) - (onField(a) || 0),
    };
  }

  public static sortDateAsc<T>(onField: (item: T) => IDate|IDateTime): ISortDefinition<T, any> {
    return {
      description: "from oldest to newest",
      icomoon: "sort-amount-asc",
      compare: (a, b) => -Sort.compareDateDesc(onField(a), onField(b)),
    };
  }

  public static sortMomentAsc<T>(onField: (item: T) => moment.Moment): ISortDefinition<T, any> {
    return {
      description: "from oldest to newest",
      icomoon: "sort-amount-asc",
      compare: (a, b) => -Sort.compareMomentDesc(onField(a), onField(b)),
    };
  }

  public static sortDateDesc<T>(onField: (item: T) => IDate|IDateTime): ISortDefinition<T, any> {
    return {
      description: "from newest to oldest",
      icomoon: "sort-amount-desc",
      compare: (a, b) => Sort.compareDateDesc(onField(a), onField(b)),
    };
  }

  public static sortMomentDesc<T>(onField: (item: T) => moment.Moment): ISortDefinition<T, any> {
    return {
      description: "from newest to oldest",
      icomoon: "sort-amount-desc",
      compare: (a, b) => Sort.compareMomentDesc(onField(a), onField(b)),
    };
  }

  public static sortBooleanAsc<T>(onField: (item: T) => boolean): ISortDefinition<T, any> {
    return {
      description: "(false first)",
      icomoon: "sort-amount-asc",
      compare: (a, b) => (onField(a) ? 1 : 0) - (onField(b) ? 1 : 0),
    };
  }

  public static sortBooleanDesc<T>(onField: (item: T) => boolean): ISortDefinition<T, any> {
    return {
      description: "(true first)",
      icomoon: "sort-amount-desc",
      compare: (a, b) => (onField(a) ? 0 : 1) - (onField(b) ? 0 : 1),
    };
  }

  public static none<T>(): ISortDefinition<T, any> {
    return {
      description: null,
      icomoon: null,
      compare: (a, b) => 0,
    };
  }
}
